import React, { useState, useEffect, useRef } from "react";
import Utils from "../../utils/Utils";
import TableCellRank from "../table/TableCellRank";
import TableSkeleton from "../table/TableSkeleton";
import Checkbox from "../form/Checkbox";

const tableHeader = [
  {
    title: "Category",
    showImage: false,
    sortable: false,
    key: "Category",
  },
  {
    title: "Offensive",
    showImage: false,
    sortable: false,
    key: "Offensive_Regression",
  },
  {
    title: "Defense",
    showImage: false,
    sortable: false,
    key: "Defense_Regression",
  },
];

const miscAnalysisCategories = [
  {
    key: "Shot Selection",
    label: "Shot Selection",
    title: "Scale 1-100",
    "displayIn%": true,
    rankKey: "Shot Selection rank",
  },
  {
    key: "Spacing",
    label: "Spacing",
    title: "Scale 1-100",
    "displayIn%": true,
    rankKey: "Spacing rank",
  },
  {
    key: "Shot_Making",
    label: "Shot Making",
    "displayIn%": true,
    rankKey: "Shot_Making rank",
  },
  {
    key: "Turnover_Percent",
    label: "TO%",
    "displayIn%": true,
    rankKey: "Turnover_Percent rank",
  },
  {
    key: "FTR",
    label: "FTR",
    "displayIn%": true,
    rankKey: "FTR rank",
  },
  {
    key: "Rebounding_Percent",
    label: "Rebounding%",
    "displayIn%": true,
    rankKey: "Rebounding_Percent rank",
  },
  {
    key: "Open_3_Rate",
    label: "Open 3 Rate",
    "displayIn%": true,
    rankKey: "Open_3_Rate rank",
  },
  {
    key: "Rim & 3 Rate",
    label: "Rim & 3 Rate",
    rankKey: "Rim & 3 Rate rank",
    "displayIn%": true,
  },
  {
    key: "Rim & 3 SQ PPP",
    label: "Rim & 3 SQ PPP",
    "displayIn%": false,
    rankKey: "Rim & 3 SQ PPP rank",
  },
  {
    key: "Coaching ATO SQ PPP",
    label: "Coaching ATO SQ PPP",
    "displayIn%": false,
    rankKey: "Coaching ATO SQ PPP rank",
  },
];

const TeamMiscAnalysis = ({
  miscDEF = {},
  miscOFF = {},
  confMiscDEF = {},
  confMiscOFF = {},
  leagueType,
  year,
  conferenceTeamCount = 0,
  loading = false,
}) => {
  const [activeMiscType, setActiveMiscType] = useState({
    DEF: miscDEF,
    OFF: miscOFF,
  });
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const checkBoxRef = useRef(null);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

  useEffect(() => {
    if (isCheckboxChecked) {
      setCheckboxChecked(true);
      setActiveMiscType({ OFF: confMiscOFF, DEF: confMiscDEF });
    } else {
      setCheckboxChecked(false);
      setActiveMiscType({ OFF: miscOFF, DEF: miscDEF });
    }
  }, [isCheckboxChecked]);

  useEffect(() => {
    if (checkboxChecked) {
      setActiveMiscType({ OFF: confMiscOFF, DEF: confMiscDEF });
    } else {
      setActiveMiscType({ OFF: miscOFF, DEF: miscDEF });
    }
  }, [confMiscOFF, confMiscDEF, miscOFF, miscDEF, year]);

  const rankConfig = {
    leagueType: checkBoxRef?.current?.checked ? "conference" : leagueType,
    conferenceTeamCount: checkBoxRef?.current?.checked ? conferenceTeamCount : 0,
  };

  const handleCheckboxClick = () => {
    return checkBoxRef.current.checked ? setIsCheckboxChecked(true) : setIsCheckboxChecked(false);
  };

  return loading ? (
    <TableSkeleton tableHeader={tableHeader} tableRowsCount={10} />
  ) : (
    <div className="row no-gutters">
      <div className="col-7 text-left text-md-left my-2 my-mb-3 mt-mb-0 d-flex align-items-center">
        <h4 style={{ fontSize: 36, fontFamily: "var(--font-oswald)" }}>Misc Analysis</h4>
      </div>
      {leagueType !== "wnba" && (
        <div className="col-5 text-center text-sm-right text-md-right my-2 my-mb-3 mt-mb-0 d-flex justify-content-end">
          <Checkbox
            id="miscAnalysisConferenceOnlyCheck"
            ref={checkBoxRef}
            onClick={handleCheckboxClick}
            label="Conference Only"
            checked={checkBoxRef?.current?.checked}
          />
        </div>
      )}

      <div className="col-12">
        <div className="table-responsive p-0 ptY-0">
          <table className="table miscAnalysis">
            <thead className={Utils.getTableHeaderStyle()} style={{ top: "0px" }}>
              <tr>
                <th
                  style={{
                    borderTopLeftRadius: "24px",
                    borderRight: "1px solid #6B6C72",
                  }}
                >
                  Category
                </th>
                <th style={{ borderRight: "1px solid #6B6C72" }}>Offensive</th>
                <th style={{ borderTopRightRadius: "24px" }}>Defensive</th>
              </tr>
            </thead>
            <tbody>
              {miscAnalysisCategories.map((category, i) => {
                if (leagueType === "wnba" && (category["label"] === "Open 3 Rate" || category["label"] === "Spacing")) {
                  return null;
                } else {
                  return (
                    <tr key={`miscanalysis${i + 1}`}>
                      <td
                        style={{
                          color: "#434343",
                          fontWeight: 700,
                          borderRight: "1px solid #D0D2D9",
                        }}
                      >
                        {category["label"]}
                      </td>
                      <TableCellRank
                        {...rankConfig}
                        rankValue={activeMiscType["OFF"][category["rankKey"]]}
                        style={{
                          borderRight: "1px solid #D0D2D9",
                          fontSize: "14px",
                          fontWeight: 400,
                        }}
                      >
                        {category["displayIn%"]
                          ? Utils.toPercent(activeMiscType["OFF"][category["key"]])
                          : Utils.toFixedNum(activeMiscType["OFF"][category["key"]])}
                      </TableCellRank>
                      <TableCellRank rankValue={activeMiscType["DEF"][category["rankKey"]]} {...rankConfig}>
                        {category["displayIn%"]
                          ? Utils.toPercent(activeMiscType["DEF"][category["key"]])
                          : Utils.toFixedNum(activeMiscType["DEF"][category["key"]])}
                      </TableCellRank>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamMiscAnalysis;
